import { useFormik } from 'formik';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import { Card } from '@components/card/card';
import { CardBody, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '@components/Shared/Breadcrumb';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  updateIssueTransactions,
  getIssueTransactionsById,
  cleanIssueTransactionsById
} from '@store/INV/Transactions/InvIssue/invIssueSlice';
import { useNavigate, useParams } from 'react-router-dom';
import useGetTransactionTypesIssueList from '@hooks/INV/InvIssue/useGetTransactionTypesIssueList';
import useGetCustomersList from '@hooks/NTX/Peoples/useGetCustomersList';
import { TreeSelect } from 'antd';
import useGetSuppliersList from '@hooks/NTX/Peoples/useGetSuppliersList';
import Loading from '@components/Shared/Loader/Loading';
import useGetSubinventoryList from '@hooks/INV/Inventories/useGetSubinventoryList';
import useGetInventoryWithPermission from '@hooks/NTX/Organization/useGetInventoryWithPermission';
import { useEffect, useMemo, useState } from 'react';
import useGetOperatingUnitWithPermission from '@hooks/NTX/Organization/useGetOperatingUnitWithPermission';
import IssueLines from './IssueLines';
import NeatixModal from '@components/Modal/NeatixModal';
import JournalsByReference from '@pages/Finance/Journals/JournalsByReference/JournalsByReference';
import ButtonPrint from '@components/Print/ButtonPrint';
import { usePermissions } from '@Helper/Permisstions/usePermissions';
import SwalAlert from '@components/Shared/Alert/SwalAlert';
import { activeFinanceSelector } from '@store/Login/authSelectors';
import FileUpload from '@components/Files/FileUpload';

type UiAttachment = { id?: string; name: string; url: string };

const mapServerAttachments = (attachment?: any[]): UiAttachment[] =>
  (attachment ?? []).map(a => ({
    id: a.id,
    name: a.fileName,
    url: a.filePath
  }));

const Update = () => {
  const activeFinance = useAppSelector(activeFinanceSelector);

  const { HasPermission } = usePermissions();
  const { Id } = useParams();
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const Navigate = useNavigate();
  const [show, setShow] = useState(false);

  const { OperatingUnitOptions, loading: loading1, error: error1 } = useGetOperatingUnitWithPermission();
  const { InventoryOptions, loading: loading2, error: error2 } = useGetInventoryWithPermission();
  const { TreeSubInventoryData, loading: loading3, error: error3 } = useGetSubinventoryList();
  const { treeCustomerData, loading: loading4, error: error4 } = useGetCustomersList();
  const { treeSuppliersData, loading: loading5, error: error5 } = useGetSuppliersList();
  const { OptionsTransactionTypes, loading: loading6, error: error6 } = useGetTransactionTypesIssueList();

  useEffect(() => {
    if (Id) {
      dispatch(getIssueTransactionsById(Id));
    }
    return () => {
      dispatch(cleanIssueTransactionsById());
    };
  }, [Id, dispatch]);

  const { InvIssueById: data, loading: loadingById, error: errorById } = useAppSelector(a => a.InvIssue);

  const formik = useFormik({
    initialValues: {
      code: data?.code?.substring(0, 3) ?? '',
      transactionDate: data.transactionDate ?? '',
      description: data.description ?? '',
      branchId: data.branchId ?? '',
      transactionAmount: data.transactionAmount ?? 0,
      referenceType: data.referenceType ?? '',
      referenceCode: data.referenceCode ?? '',
      referenceId: data.referenceId ?? '',
      supplierId: data.supplierId ?? '',
      customerId: data.customerId ?? '',
      personId: data.personId ?? '',
      subInventoryId: data.subInventoryId ?? '',
      inventoryId: data.inventoryId ?? '',
      address: data.address ?? '',
      note: data.note ?? '',
      supplierInvoiceNum: data.supplierInvoiceNum ?? '',
      transactionYear: 0,
      postedFlag: data ? data.postedFlag : false,

      // ... باقي الحقول
      attachments: mapServerAttachments(data?.attachment), // ✅ ملفات موجودة
      fileAttachment: [] as File[], // ✅ ملفات جديدة (رفع)
      //
      IsUpdateAttachment: false,
      materialTransactionsLineList:
        data?.materialTransactionsLineList?.map(item => ({
          id: item.id,
          transactionItemId: item.transactionItemId,
          transactionUom: item.transactionUom,
          unitPrice: item.unitPrice,
          transactionQuantityIn: item.transactionQuantityIn,
          transactionQuantityOut: item.transactionQuantityOut,
          inventoryId: item.inventoryId,
          discount: item.discount,
          codeCombinationId: item.codeCombinationId,
          currencyCode: item.currencyCode,
          currencyConversionRate: item.currencyConversionRate,
          referenceIdMaster: item.referenceIdMaster,
          referenceIdLine: item.referenceIdLine,
          transactionTypeId: item.transactionTypeId,
          equivalent: item.equivalent,
          expensesAmount: item.expensesAmount,
          batchId: item.batchId
        })) ?? []
    },

    enableReinitialize: true,
    onSubmit: values => {
      if (data.postedFlag === true) {
        SwalAlert({
          text: t('CannotEditAfterPosting')
        });
        formik.setSubmitting(false);
        return;
      }
      dispatch(
        updateIssueTransactions({
          id: data.id,
          code: values.code,
          transactionDate: values.transactionDate,
          description: values.description,
          branchId: values.branchId,
          transactionAmount: 0,
          supplierId: values.supplierId || null,
          customerId: values.customerId || null,
          personId: values.personId || null,
          inventoryId: values.inventoryId,
          address: values.address,
          note: values.note,
          //
          fileAttachment: values.fileAttachment,
          IsUpdateAttachment: values.IsUpdateAttachment,
          //
          materialTransactionsLineList: formik.values.materialTransactionsLineList?.map(item => ({
            id: item.id,
            transactionItemId: item.transactionItemId,
            transactionUom: item.transactionUom,
            // unitPrice: 0,
            unitPrice: item.unitPrice,
            transactionQuantityIn: item.transactionQuantityIn,
            transactionQuantityOut: 0,
            referenceIdMaster: item.referenceIdMaster || null,
            referenceIdLine: item.referenceIdLine || null,

            inventoryId: null,
            discount: 0,
            currencyCode: '',
            currencyConversionRate: 0,
            // referenceIdMaster: null,
            // referenceIdLine: null,
            transactionTypeId: null,
            batchId: null
          }))
        })
      )
        .unwrap()
        .then((res: any) => {
          if (res?.succeeded === true) {
            CustomAlert({ action: 'Edit' });
            Navigate(-1);
          } else {
            formik.setSubmitting(false);
            CustomAlert({ action: 'Error', msg: res?.message });
          }
        })
        .catch((error: any) => {
          formik.setSubmitting(false);
          CustomAlert({ action: 'Error' });
        });
    }
  });

  const { values, handleChange: formikhandleChange, errors, touched } = formik;

  const isLoading = useMemo(
    () => loadingById || loading1 || loading2 || loading3 || loading4 || loading5 || loading6,
    [loadingById, loading1, loading2, loading3, loading4, loading5, loading6]
  );

  const isError = useMemo(
    () => errorById || error1 || error2 || error3 || error4 || error5 || error6,
    [errorById, error1, error2, error3, error4, error5, error6]
  );

  const selectedOU = OperatingUnitOptions?.find((x: any) => x.value === formik.values.branchId);
  const selectedInv = InventoryOptions?.find((x: any) => x.value === formik.values.inventoryId);
  const selectedType = OptionsTransactionTypes?.find((x: any) => x.value === formik.values.code);

  return (
    <>
      <div className='d-flex align-items-center'>
        <Breadcrumb
          BreadcrumbList={[
            {
              name: t('Home'),
              link: ':moduleHome'
            },
            {
              name: t('InvIssue'),
              link: ''
            },
            {
              name: t('UpdateInvIssue'),
              link: null
            }
          ]}
          PageName={t('EditIssuePermission')}
          subtitle={data.code}
        />
        <div className='ms-auto'>
          <ButtonPrint PrintTemplateTypeId='E38B463D-24B8-436B-A74B-54E5C0A4802B' ReferenceId={Id || ''} />
        </div>
      </div>
      <Loading loading={isLoading} error={isError}>
        <Form onSubmit={formik.handleSubmit}>
          <Card>
            {data.postedFlag === true && (
              <div className='alert alert-info d-flex align-items-center justify-content-between m-2'>
                <div className='d-flex align-items-center'>
                  <i className='fas fa-info-circle fa-2x me-3'></i>
                  <div>
                    {/* <h5 className='alert-heading'>{t('عملية مرحلة')}</h5>
                  <p className='mb-0'>{t('لا يمكن تعديل عمليات الصرف المرحلة في النظام')}</p> */}

                    <h5 className='alert-heading'>{i18n.language === 'en' ? 'Posted Transaction' : 'عملية مرحلة'}</h5>
                    <p className='mb-0'>
                      {i18n.language === 'en'
                        ? 'Posted disbursement transactions cannot be modified in the system.'
                        : 'لا يمكن تعديل عمليات الصرف المرحلة في النظام'}
                    </p>
                  </div>
                </div>

                {/* الزر في الطرف الآخر */}
                <button
                  hidden={activeFinance !== true}
                  type='button'
                  className='btn btn-sm btn-info'
                  onClick={() => setShow(true)}
                >
                  <i className='fa fa-info-circle fa-fw me-1'></i>
                  {t('JournalDetails')}
                </button>
              </div>
            )}

            <CardBody>
              {/* ✅ ملخص الاختيارات من  */}
              <Card className='mb-3 border-0 bg-theme bg-opacity-10'>
                <div className='p-3 d-flex flex-wrap gap-4 align-items-center justify-content-between'>
                  <div>
                    <div className='small text-muted'>{t('OperatingUnit')}</div>
                    <div className='fw-bold'>{selectedOU?.label ?? '-'}</div>
                  </div>

                  <div>
                    <div className='small text-muted'>{t('Inventory')}</div>
                    <div className='fw-bold'>{selectedInv?.label ?? '-'}</div>
                  </div>

                  <div>
                    <div className='small text-muted'>{t('TransactionType')}</div>
                    <div className='fw-bold'>{selectedType?.label ?? '-'}</div>
                  </div>

                  <div>
                    <div className='small text-muted'>{t('Reference')}</div>
                    <div className='fw-bold'>{formik.values.referenceCode || formik.values.referenceId || '-'}</div>
                  </div>
                </div>
              </Card>
              <div className='row'>
                <div className='col-lg-3'>
                  <div className='form-group mb-3'>
                    <label className='form-label'>{t('Date')}</label>
                    <Form.Control
                      disabled={data.postedFlag === true}
                      className='form-control '
                      type='date'
                      name='transactionDate'
                      onChange={formikhandleChange}
                      value={values.transactionDate?.toString()?.split('T')[0] || ''}
                      isInvalid={!!(touched.transactionDate && errors.transactionDate)}
                    />
                  </div>
                </div>

                <div className='col-lg-3'>
                  <div className='form-group mb-3'>
                    <label className='form-label'>{t('SubInventory')}</label>
                    <TreeSelect
                      loading={loading3}
                      disabled
                      showSearch
                      className='w-100'
                      treeLine
                      placeholder={t('Choose')}
                      allowClear
                      treeDefaultExpandAll={false}
                      value={values.subInventoryId}
                      treeData={TreeSubInventoryData?.filter(f => f.parentId === values.inventoryId)}
                      treeNodeFilterProp='title'
                    />
                  </div>
                </div>

                {['SPR', ''].includes(values.code) && (
                  <div className='col-lg-3'>
                    <div className='form-group mb-3'>
                      <label className='form-label'>{t('Supplier')}</label>
                      <TreeSelect
                        loading={loading5}
                        disabled
                        showSearch
                        className='w-100'
                        treeLine
                        placeholder={t('Choose')}
                        allowClear
                        treeDefaultExpandAll={false}
                        value={values.supplierId}
                        onChange={(option: any) => {
                          formik.setFieldValue('supplierId', option === null ? null : option);
                        }}
                        treeData={treeSuppliersData}
                        treeNodeFilterProp='title'
                      />
                    </div>
                  </div>
                )}

                {['INV', 'SOI'].includes(values.code) && (
                  <div className='col-lg-3'>
                    <div className='form-group mb-3'>
                      <label className='form-label'>{t('Customer')}</label>
                      <TreeSelect
                        loading={loading4}
                        disabled
                        showSearch
                        className='w-100'
                        treeLine
                        allowClear
                        treeDefaultExpandAll={false}
                        value={values.customerId}
                        onChange={(option: any) => {
                          formik.setFieldValue('customerId', option === null ? null : option);
                        }}
                        treeData={treeCustomerData}
                        treeNodeFilterProp='title'
                        placeholder={t('Choose')}
                      />
                    </div>
                  </div>
                )}

                <div className='col-lg-6'>
                  <div className='form-group mb-3'>
                    <label className='form-label'>{t('Note')}</label>
                    <Form.Control
                      autoComplete='off'
                      className='form-control'
                      as='textarea'
                      rows={1}
                      name='description'
                      onChange={formik.handleChange}
                      value={values.description || ''}
                      isInvalid={!!(touched.description && errors.description)}
                      isValid={touched.description && !errors.description}
                    />
                    <div></div>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
          <Card className='pt-1 mt-2 shadow-sm '>
            {values.materialTransactionsLineList.length > 0 && <IssueLines formik={formik} isUpdate={true} />}
          </Card>

          <Card className='mt-3'>
            <div className='card-header'>
              <span className='m-0'>{t('Attachments')}</span>
            </div>

            <CardBody>
              {/* لو العملية مرحلة/posted تقدر تعرضها قراءة فقط */}
              {data?.postedFlag === true ? (
                <ul className='list-group'>
                  {(formik.values.attachments ?? []).map((a: any, i: number) => (
                    <li key={i} className='list-group-item d-flex justify-content-between align-items-center'>
                      {'url' in a ? (
                        <a href={a.url} target='_blank' rel='noopener noreferrer' className='text-decoration-none'>
                          {a.name}
                        </a>
                      ) : (
                        <span>{a.name}</span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <FileUpload
                  attachments={formik.values.attachments}
                  // setAttachments={setFormikAttachments}
                  setAttachments={(updater: any) => {
                    const next = typeof updater === 'function' ? updater(formik.values.fileAttachment) : updater;

                    formik.setFieldValue('fileAttachment', next);
                    formik.setFieldValue('IsUpdateAttachment', true);
                    // اختياري لو عندك فاليديشن على المرفقات
                    // formik.setFieldTouched('fileAttachment', true, true);
                  }}
                  multiple={true}
                />
              )}
            </CardBody>
          </Card>
          <Card className='p-2 mt-2'>
            <div className='text-end'>
              {HasPermission('INVEditInvIssue') && (
                <button
                  disabled={formik.isSubmitting || data?.postedFlag}
                  type='submit'
                  className='btn btn-lg me-1 btn-theme rounded-2'
                >
                  {formik.isSubmitting ? (
                    <div className='spinner-border spinner-border-sm me-2'></div>
                  ) : data?.postedFlag ? (
                    <i className='fas fa-check-circle fa-lg fa-fw me-2'></i>
                  ) : (
                    <i className='fas fa-save fa-lg fa-fw me-2'></i>
                  )}
                  {data?.postedFlag ? t('RecordPosted') : t('Save')}
                </button>
              )}
              <button type='button' className='btn btn-lg me-1 btn-default rounded-2' onClick={() => Navigate(-1)}>
                <i className='fa fa-power-off fa-fw me-1'></i> {t('Cancel')}
              </button>
            </div>
          </Card>
        </Form>
      </Loading>
      <NeatixModal show={show} setShow={setShow} size='xl'>
        {Id && <JournalsByReference referenceId={Id} />}
      </NeatixModal>
    </>
  );
};

export default Update;

// const Update = () => {
//   const { HasPermission } = usePermissions();
//   const { Id } = useParams();
//   const { t, i18n } = useTranslation();
//   const dispatch = useAppDispatch();
//   const Navigate = useNavigate();
//   const [show, setShow] = useState(false);

//   const { dataEmployees, loading, error } = useGetPersons();
//   const { OperatingUnitOptions, loading: loading1, error: error1 } = useGetOperatingUnitWithPermission();
//   const { InventoryOptions, loading: loading2, error: error2 } = useGetInventoryWithPermission();
//   const { TreeSubInventoryData, loading: loading3, error: error3 } = useGetSubinventoryList();
//   const { treeCustomerData, loading: loading4, error: error4 } = useGetCustomersList();
//   const { treeSuppliersData, loading: loading5, error: error5 } = useGetSuppliersList();
//   const { OptionsTransactionTypes, loading: loading6, error: error6 } = useGetTransactionTypesIssueList();

//   //~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//   useEffect(() => {
//     if (Id) {
//       dispatch(getIssueTransactionsById(Id));
//     }
//     return () => {
//       dispatch(cleanIssueTransactionsById());
//     };
//   }, [Id, dispatch]);

//   const { InvIssueById: data, loading: loadingById, error: errorById } = useAppSelector(a => a.InvIssue);

//   //#region Formik
//   const formik = useFormik({
//     initialValues: {
//       code: data?.code?.substring(0, 3) ?? '', // "SOI20244" => "SOI"
//       transactionDate: data.transactionDate ?? '',
//       description: data.description ?? '',
//       branchId: data.branchId ?? '',
//       transactionAmount: data.transactionAmount ?? 0,
//       referenceType: data.referenceType ?? '',
//       referenceCode: data.referenceCode ?? '',
//       referenceId: data.referenceId ?? '',
//       supplierId: data.supplierId ?? '',
//       customerId: data.customerId ?? '',
//       personId: data.personId ?? '',
//       subInventoryId: data.subInventoryId ?? '',
//       inventoryId: data.inventoryId ?? '',
//       address: data.address ?? '',
//       note: data.note ?? '',
//       transactionYear: 0,
//       materialTransactionsLineList:
//         data?.materialTransactionsLineList?.map(item => ({
//           id: item.id, //
//           transactionItemId: item.transactionItemId,
//           transactionUom: item.transactionUom,
//           unitPrice: item.unitPrice,
//           transactionQuantityIn: item.transactionQuantityIn,
//           transactionQuantityOut: item.transactionQuantityOut,

//           inventoryId: item.inventoryId,
//           discount: item.discount,
//           codeCombinationId: item.codeCombinationId,
//           currencyCode: item.currencyCode,
//           currencyConversionRate: item.currencyConversionRate,
//           referenceIdMaster: item.referenceIdMaster,
//           referenceIdLine: item.referenceIdLine,
//           transactionTypeId: item.transactionTypeId,
//           equivalent: item.equivalent,
//           expensesAmount: item.expensesAmount,
//           batchId: item.batchId
//         })) ?? []
//     },

//     enableReinitialize: true,
//     // validationSchema: AddGlJeHeaderSchema(t),
//     onSubmit: values => {
//       dispatch(
//         updateIssueTransactions({
//           id: data.id,
//           code: values.code,
//           transactionDate: values.transactionDate,
//           description: values.description,
//           branchId: values.branchId,
//           transactionAmount: 0,
//           supplierId: values.supplierId || null,
//           customerId: values.customerId || null,
//           personId: values.personId || null,
//           inventoryId: values.inventoryId,
//           address: values.address,
//           note: values.note,
//           // referenceCode: values.referenceCode,
//           // referenceType: values.referenceType,
//           // referenceId: values.referenceId || null,
//           // no: 0,
//           // transactionYear: 0,

//           materialTransactionsLineList: formik.values.materialTransactionsLineList?.map(item => ({
//             id: item.id,
//             transactionItemId: item.transactionItemId,
//             transactionUom: item.transactionUom,
//             unitPrice: 0, // item.price
//             transactionQuantityIn: item.transactionQuantityIn,
//             transactionQuantityOut: 0,
//             inventoryId: null,
//             discount: 0,
//             currencyCode: '',
//             currencyConversionRate: 0,
//             referenceIdMaster: null,
//             referenceIdLine: null,
//             transactionTypeId: null,
//             batchId: null
//             // codeCombinationId: null,
//             // equivalent: 0,
//             // expensesAmount: 0,
//           }))
//         })
//       )
//         .unwrap()
//         .then((res: any) => {
//           // debugger;
//           if (res?.succeeded === true) {
//             CustomAlert({ action: 'Add' });
//             formik.resetForm();
//           } else {
//             formik.setSubmitting(false);
//             CustomAlert({ action: 'Error', msg: res?.message });
//           }
//         })
//         .catch((error: any) => {
//           formik.setSubmitting(false);
//           CustomAlert({ action: 'Error' });
//         });
//     }
//   });

//   const { values, handleChange: formikhandleChange, errors, touched } = formik;
//   //#endregion

//   //____________________________
//   const BcrumbList = [
//     {
//       name: 'Home',
//      link: ':moduleHome'
//     },
//     {
//       name: 'InvIssue',
//       link: ''
//     },
//     {
//       name: 'Update InvIssue',
//       link: null
//     }
//   ];

//   const isLoading = useMemo(
//     () => loadingById || loading || loading1 || loading2 || loading3 || loading4 || loading5 || loading6,
//     [loadingById, loading, loading1, loading2, loading3, loading4, loading5, loading6]
//   );

//   const isError = useMemo(
//     () => errorById || error || error1 || error2 || error3 || error4 || error5 || error6,
//     [errorById, error, error1, error2, error3, error4, error5, error6]
//   );

//   return (
//     <>
//       <div className='d-flex align-items-center'>
//         <Breadcrumb BreadcrumbList={BcrumbList} PageName='تعديل إذن صـرف' subtitle={data.code} />
//         {/* {true && ( */}
//         <div className='ms-auto'>
//           <Link to='' className='btn me-1 btn-theme' onClick={() => setShow(true)}>
//             <i className='fa fa-info-circle fa-fw me-1'></i>
//             تفاصيـل القيد
//           </Link>
//           <ButtonPrint PrintTemplateTypeId='E38B463D-24B8-436B-A74B-54E5C0A4802B' ReferenceId={Id || ''} />
//         </div>
//         {/* // )} */}
//       </div>

//       <Card>
//         <Loading loading={isLoading} error={isError}>
//           <CardBody>
//             <Form onSubmit={formik.handleSubmit}>
//               <div className='row'>
//                 <div className='col-lg-3'>
//                   <div className='form-group mb-3'>
//                     <label className='form-label'>{t('التاريـخ')}</label>
//                     <Form.Control
//                       className='form-control '
//                       type='date'
//                       name='transactionDate'
//                       onChange={formikhandleChange}
//                       value={values.transactionDate?.toString()?.split('T')[0] || ''}
//                       isInvalid={!!(touched.transactionDate && errors.transactionDate)}
//                     />
//                   </div>
//                 </div>

//                 <div className='col-lg-3'>
//                   <div className='form-group mb-3'>
//                     <label className='form-label'>{t('الفرع')}</label>
//                     <Select
//                       isLoading={loading1}
//                       isDisabled
//                       classNamePrefix='react-select'
//                       isSearchable={true}
//                       isClearable
//                       options={OperatingUnitOptions}
//                       value={OperatingUnitOptions?.find((f: any) => f.value === formik.values.branchId)}
//                       // onChange={(option: any) => {
//                       //   formik.setFieldValue('branchId', option === null ? null : option.value);
//                       // }}
//                       placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
//                     />
//                   </div>
//                 </div>

//                 <div className='col-lg-3'>
//                   <div className='form-group mb-3'>
//                     <label className='form-label'>{t('المخزن')}</label>
//                     <Select
//                       isLoading={loading2}
//                       isDisabled
//                       classNamePrefix='react-select'
//                       isSearchable={true}
//                       isClearable
//                       options={InventoryOptions}
//                       value={InventoryOptions?.find((f: any) => f.value === values.inventoryId)}
//                       // onChange={(option: any) => {
//                       //   formik.setFieldValue('inventoryId', option === null ? null : option.value);
//                       //   formik.setFieldValue('subInventoryId', null);
//                       // }}
//                       placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
//                     />
//                   </div>
//                 </div>

//                 <div className='col-lg-3'>
//                   <div className='form-group mb-3'>
//                     <label className='form-label'>{t('المخزن الفرعي')}</label>
//                     <TreeSelect
//                       loading={loading3}
//                       disabled
//                       showSearch
//                       className='w-100'
//                       treeLine
//                       placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
//                       allowClear
//                       treeDefaultExpandAll={false}
//                       value={values.subInventoryId}
//                       // onChange={(option: any) => {
//                       //   formik.setFieldValue('subInventoryId', option === null ? null : option);
//                       // }}
//                       treeData={TreeSubInventoryData?.filter(f => f.parentId === values.inventoryId)}
//                       treeNodeFilterProp='title'
//                     />
//                   </div>
//                 </div>

//                 <div className='col-lg-3'>
//                   <div className='form-group mb-3'>
//                     <label className='form-label'>{t('نوع الحركة')}</label>
//                     <Select
//                       isDisabled
//                       classNamePrefix='react-select'
//                       isLoading={loading6}
//                       isSearchable={true}
//                       isClearable
//                       options={OptionsTransactionTypes}
//                       value={OptionsTransactionTypes?.find(f => f.transTypeId === data.transactionTypeId)}
//                       placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
//                     />
//                   </div>
//                 </div>

//                 <div className='col-lg-3'>
//                   <div className='form-group mb-3'>
//                     <label className='form-label'>{t('المرجـع')}</label>
//                     <Form.Control
//                       className='form-control'
//                       type='text'
//                       onChange={formikhandleChange}
//                       value={values.referenceCode || ''}
//                     />
//                   </div>
//                 </div>

//                 {/* <div className='col-xl-3'>
//                   <div className='form-group mb-3'>
//                     <Reference formik={formik} isUpdate={true} />
//                   </div>
//                 </div> */}

//                 {['SPR', ''].includes(values.code) && (
//                   <div className='col-lg-3'>
//                     <div className='form-group mb-3'>
//                       <label className='form-label'>{t('المورد')}</label>
//                       <TreeSelect
//                         loading={loading5}
//                         disabled
//                         showSearch
//                         className='w-100'
//                         treeLine
//                         placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
//                         allowClear
//                         treeDefaultExpandAll={false} // لا تقم بتوسيع كل العقد افتراضيًا
//                         value={values.supplierId}
//                         onChange={(option: any) => {
//                           formik.setFieldValue('supplierId', option === null ? null : option);
//                         }}
//                         treeData={treeSuppliersData}
//                         treeNodeFilterProp='title'
//                       />
//                     </div>
//                   </div>
//                 )}

//                 {['INV', 'SOI'].includes(values.code) && (
//                   <div className='col-lg-3'>
//                     <div className='form-group mb-3'>
//                       <label className='form-label'>{t('العميل')}</label>

//                       <TreeSelect
//                         loading={loading4}
//                         disabled
//                         showSearch
//                         className='w-100'
//                         treeLine
//                         allowClear
//                         treeDefaultExpandAll={false} // لا تقم بتوسيع كل العقد افتراضيًا
//                         value={values.customerId}
//                         onChange={(option: any) => {
//                           formik.setFieldValue('customerId', option === null ? null : option);
//                         }}
//                         treeData={treeCustomerData}
//                         treeNodeFilterProp='title'
//                         placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
//                       />
//                     </div>
//                   </div>
//                 )}

//                 <div className='col-lg-3'>
//                   <div className='form-group mb-3'>
//                     <label className='form-label'>{t('personId - store keeper')}</label>
//                     <Select
//                       isLoading={loading}
//                       classNamePrefix='react-select'
//                       isSearchable={true}
//                       isClearable
//                       name='personId'
//                       options={dataEmployees}
//                       value={dataEmployees?.find((f: any) => f.value === values.personId)}
//                       onChange={(option: any) => {
//                         formik.setFieldValue('personId', option === null ? null : option.value);
//                       }}
//                       placeholder={i18n.language === 'ar' ? 'إختر' : 'Choose'}
//                     />
//                   </div>
//                 </div>
//                 <div className='col-lg-12'>
//                   <div className='form-group mb-3'>
//                     <label className='form-label'>{t('البيــان')}</label>
//                     <Form.Control
//                       autoComplete='off'
//                       className='form-control'
//                       as='textarea'
//                       rows={1}
//                       name='description'
//                       onChange={formik.handleChange}
//                       value={values.description || ''}
//                       isInvalid={!!(touched.description && errors.description)}
//                       isValid={touched.description && !errors.description}
//                     />
//                     <div></div>
//                   </div>
//                 </div>
//               </div>

//               <div className='row m-0 p-0'>
//                 <div className='bg-inverse bg-opacity-5 p-2 position-relative'>
//                   {values.materialTransactionsLineList.length > 0 && <IssueLines formik={formik} isUpdate={true} />}
//                 </div>
//               </div>

//               <div className='text-center mt-4'>
//                 {HasPermission('INVEditInvIssue') && (
//                   <button disabled={formik.isSubmitting} type='submit' className='btn   me-1 btn-theme mb-1'>
//                     {formik.isSubmitting ? (
//                       <div className='spinner-border spinner-border-sm me-2'></div>
//                     ) : (
//                       <i className='fas fa-lg fa-fw me-2 fa-save'></i>
//                     )}
//                     {t('Save')}
//                   </button>
//                 )}
//                 <button type='button' className='btn    me-1 btn-default mb-1' onClick={() => Navigate(-1)}>
//                   <i className='fa fa-power-off fa-fw me-1'></i> {t('Cancel')}
//                 </button>
//               </div>
//             </Form>
//           </CardBody>
//         </Loading>
//       </Card>
//       <NeatixModal show={show} setShow={setShow} size='xl'>
//         {Id && <JournalsByReference referenceId={Id} />}
//       </NeatixModal>
//     </>
//   );
// };
